import React, { Fragment } from 'react';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import {
  Body,
  Footnote,
  Caption1,
  Subhead,
  Title3,
} from '../../../components/Typography';
import { EditIcon } from '../../../components/Icons';
import { PICKUP, DELIVERY, ASAP, DROPOFF } from '../orderSettingTypes';
import Tooltip from '@material-ui/core/Tooltip';
import { isLocationAcceptingOrders } from '../OrderSettingModal/utils/helper';
import appConfig from '../../../../app-config';
import { isGuestOrderInfoClient } from '../../../clientCustomizations';

const styles = {
  tooltip: {
    width: '220px',
    position: 'absolute',
    zIndex: 1,
    left: '-180px',
    bottom: '150%',
  },
  arrow: {
    '&::before': {
      position: 'absolute',
      right: '-180px',
    },
  },
};

const CustomTooltip = withStyles(styles)(Tooltip);

const DesktopWrapper = styled.div`
  display: grid;
  grid-gap: ${(props) => (props.isForReviewPage ? '0' : '12px')};
  grid-template-columns: ${(props) =>
    props.isForReviewPage ? '150px 1fr' : '1fr'};
  height: 100%;

  @media (max-width: 1100px) {
    display: ${(props) => (props.isForReviewPage ? 'grid' : 'none')};
  }
  @media (max-width: 768px) {
    grid-gap: 21px;
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
`;

const MobileWrapper = styled.div`
  display: ${(props) => (props.isForReviewPage ? 'none' : 'flex')};
  justify-content: space-between;
  align-items: center;
  margin: 0 10%;
  @media (min-width: 1101px) {
    display: none;
  }
`;

const Closed = styled.span``;

const Edit = styled(Subhead)`
  color: ${(props) => props.theme.orderConfigEditColor};
  font-weight: 400;
  cursor: pointer;
`;

const InfoWrapper = styled.div`
  padding: ${(props) => (props.isForReviewPage ? '0 0 0 80px' : '0')};

  @media (max-width: 786px) {
    padding: 0;
  }
`;

const Title3Wrapper = styled(Title3)`
  text-transform: ${(props) => props.theme.reviewCheckoutPageTitle};
  margin-bottom: auto;
  font-weight: 600;
  width: max-content;
  min-width: max-content;

  @media (max-width: 786px) {
    width: max-content;
  }

  :after {
    content: '';
    display: block;
    width: calc(100% + 2px);
    padding-top: 2px;
    border-bottom: ${(props) => props.categoriesBarSelectedBorderColorWidth}
      solid ${(props) => props.theme.categoriesBarSelectedBorderColor};
  }
`;

const EditDiv = styled.div`
  color: ${(props) => props.theme.editIconFontColor};
  font-family: ${(props) => props.theme.primaryFontFontFamily};
  font-weight: 600;
  cursor: pointer;
`;

const FootnoteWrapper = styled(Footnote)`
  font-weight: 600;
  text-transform: ${(props) => props.theme.orderConfigFootNote};
  margin-bottom: 4px;
`;

const SpaceDiv = styled.div`
  margin: 20px;
`;

const OrderSettingSummary = ({
  notEditable,
  isForReviewPage,
  updateOrderSettingModalIsVisible,
  interPickUpDetails,
  deliveryDetails,
  orderType,
  dateTimeToShow,
  specialHours,
  parentLocation,
  dropOffDetails,
  interSelectedDateTime,
}) => {
  let { line1, city, state, line2, details } =
    orderType === PICKUP && interPickUpDetails
      ? interPickUpDetails
      : orderType === DELIVERY && deliveryDetails
      ? deliveryDetails
      : orderType === DROPOFF && dropOffDetails
      ? dropOffDetails
      : { line1: null, city: null, state: null, line2: null, details: null };

  const { name, accepting_orders, is_order_ahead_enabled } =
    orderType === PICKUP && interPickUpDetails
      ? interPickUpDetails
      : orderType === DELIVERY &&
        deliveryDetails &&
        deliveryDetails.deliveryBusiness
      ? deliveryDetails.deliveryBusiness || {
          name: '',
          accepting_orders: true,
          is_order_ahead_enabled: true,
        }
      : orderType === DROPOFF && dropOffDetails
      ? dropOffDetails
      : { name: '', accepting_orders: false, is_order_ahead_enabled: false };

  let parentLine1 = parentLocation ? parentLocation.line1 : null;
  let parentCity = parentLocation ? parentLocation.city : null;
  let parentState = parentLocation ? parentLocation.state : null;
  const line2Formated = line2 ? 'Apt/Suite ' + line2 : null;
  const deliverToAddress = [line2Formated, line1, city, state]
    .filter(Boolean)
    .join(', ');

  const renderPickUp = () => (
    <InfoWrapper isForReviewPage={isForReviewPage}>
      <FootnoteWrapper>
        {appConfig?.pickingUpFrom ?? 'Picking up from:'}
      </FootnoteWrapper>
      <Caption1>
        {(!accepting_orders ||
          !is_order_ahead_enabled ||
          !isLocationAcceptingOrders(accepting_orders, specialHours)) &&
          !interSelectedDateTime.dateTime.time.name.length && (
            <Closed>💤 Closed - </Closed>
          )}

        {appConfig.isParentChildClient ? interPickUpDetails.name : name}
      </Caption1>
      <Caption1>
        {line1}, {city}, {state}
      </Caption1>
      {appConfig.isParentChildClient && orderType != PICKUP ? (
        <Fragment>
          <FootnoteWrapper style={{ marginTop: '5px' }}>
            Located in:
          </FootnoteWrapper>

          <div>
            {name}
            <div>
              {parentLine1}, {parentCity}, {parentState}
            </div>
          </div>
        </Fragment>
      ) : null}

      <SpaceDiv />

      <>
        <FootnoteWrapper>Pick Up Time:</FootnoteWrapper>
        <Caption1 style={{ textTransform: 'capitalize' }}>
          {dateTimeToShow.name}
        </Caption1>
      </>
      {!notEditable && isForReviewPage && (
        <div
          style={{
            display: 'flex',
            marginTop: '7px',
          }}
          onClick={() => updateOrderSettingModalIsVisible(true)}
        >
          <div style={{ marginRight: '5px' }}>
            <EditIcon />
          </div>
          <Edit>Edit</Edit>
        </div>
      )}
    </InfoWrapper>
  );
  const renderDelivery = () => (
    <InfoWrapper isForReviewPage={isForReviewPage}>
      <FootnoteWrapper>Delivery To:</FootnoteWrapper>
      <Caption1>
        {(!accepting_orders || !is_order_ahead_enabled) &&
          !interSelectedDateTime.dateTime.time.name.length && (
            <Closed>💤 Currently Unavailable - </Closed>
          )}
        {deliveryDetails?.type?.toLowerCase() || null}
      </Caption1>
      <Caption1>{deliverToAddress || null}</Caption1>
      {details && (
        <Caption1>
          <st>Note: </st> {details || null}
        </Caption1>
      )}
      <SpaceDiv />
      <FootnoteWrapper>Ordering From:</FootnoteWrapper>
      <Caption1>
        {deliveryDetails && deliveryDetails.deliveryBusiness
          ? deliveryDetails?.deliveryBusiness.name
          : null}
      </Caption1>
      <SpaceDiv />
      <>
        <FootnoteWrapper>Delivery Time:</FootnoteWrapper>
        <Caption1 style={{ textTransform: 'capitalize' }}>
          {dateTimeToShow.name}
        </Caption1>
      </>
      {!notEditable && isForReviewPage && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gridGap: '10px',
            marginTop: '7px',
          }}
        >
          <div>
            <EditIcon onClick={() => updateOrderSettingModalIsVisible(true)} />
          </div>
          <Edit onClick={() => updateOrderSettingModalIsVisible(true)}>
            Edit
          </Edit>
        </div>
      )}
    </InfoWrapper>
  );
  const renderDropOff = () => (
    <InfoWrapper isForReviewPage={isForReviewPage}>
      <FootnoteWrapper>{appConfig.droppingOffTo ?? 'Dine-in:'}</FootnoteWrapper>
      <Caption1>
        {(!accepting_orders ||
          !is_order_ahead_enabled ||
          !isLocationAcceptingOrders(accepting_orders, specialHours)) &&
          !interSelectedDateTime.dateTime?.time?.name?.length && (
            <Closed>💤 Closed - </Closed>
          )}
        {name}
      </Caption1>
      <Caption1>
        {`${line1}, ${city}, ${state} \n
        ${dropOffDetails.finalDropOffLocation?.map((selection, i) => {
          if (dropOffDetails.finalDropOffLocation.length === i + 1)
            return selection.value;
          return selection.value + ', ';
        })}`}
      </Caption1>
      <SpaceDiv />

      <>
        <FootnoteWrapper>Order Time:</FootnoteWrapper>
        <Caption1 style={{ textTransform: 'capitalize' }}>
          {dateTimeToShow.name}
        </Caption1>
      </>

      {!notEditable && isForReviewPage && (
        <div
          style={{
            display: 'flex',
            marginTop: '7px',
          }}
          onClick={() => updateOrderSettingModalIsVisible(true)}
        >
          <div style={{ marginRight: '5px' }}>
            <EditIcon />
          </div>
          <Edit>Edit</Edit>
        </div>
      )}
    </InfoWrapper>
  );

  const renderPickUpOrDelivery = () => {
    return orderType === PICKUP
      ? renderPickUp()
      : orderType === DELIVERY
      ? renderDelivery()
      : orderType === DROPOFF
      ? renderDropOff()
      : null;
  };

  return (
    <Fragment>
      <DesktopWrapper isForReviewPage={isForReviewPage}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isForReviewPage ? (
            <Title3Wrapper>Order Method</Title3Wrapper>
          ) : (
            <Title3 style={{ fontWeight: '600' }}>Order Method</Title3>
          )}

          {!isForReviewPage && (
            <EditDiv onClick={() => updateOrderSettingModalIsVisible(true)}>
              <EditIcon /> Edit
            </EditDiv>
          )}
        </div>
        <div>{renderPickUpOrDelivery()}</div>
      </DesktopWrapper>
      <MobileWrapper
        onClick={() => {
          // hideToolTip();
          updateOrderSettingModalIsVisible(true);
        }}
        isForReviewPage={isForReviewPage}
      >
        {renderPickUpOrDelivery()}
        <div>
          <i className='fal fa-chevron-down' />
        </div>
      </MobileWrapper>
    </Fragment>
  );
};

export default OrderSettingSummary;
